import { noop } from '@aloft/toolkit-shared';
import { Flex, IconButton, Switch, Typography } from '@aloft/toolkit-sui';

import { FieldPath, useWatch } from 'react-hook-form';

import { useAppDispatch } from '../../../redux/hooks.ts';
import { PropertyGridForm, PropertyGridSectionProps } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';

type TitleRowProps = Pick<
  PropertyGridSectionProps,
  'columnIndex' | 'feature' | 'fieldTypes' | 'isRemovable' | 'onRemove' | 'control' | 'getValues'
> & {
  title: string;
};

const RemoveFeatureSectionComponent = ({
  columnIndex,
  title,
  feature,
  onRemove = noop,
  control,
  getValues,
}: TitleRowProps) => {
  const dispatch = useAppDispatch();

  const column = useWatch({ control, name: `columns.${columnIndex}` });
  const { propertyData, propertyId, type } = column ?? {};

  // Always call hooks unconditionally - compute the field name even if we won't use it
  const hasFeatureKey = feature === 'manualCost' ? 'hasManualCost' : `${feature}.hasFeature`;
  const hasFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;

  if (!propertyData || !propertyId || !type) return null;

  if (propertyId === 'subject') {
    return (
      <IconButton
        ariaLabel="Remove feature"
        title="Remove feature"
        icon="remove"
        iconColor="textError"
        variant="tertiary"
        onClick={onRemove}
      />
    );
  }

  if (type === 'property' && feature) {
    return (
      <Flex>
        <Switch
          id={`no-${title}-${columnIndex}`}
          label={`No ${title}`}
          value={!hasFeature}
          onChange={(value) => {
            if (propertyData && feature) {
              if (feature === 'manualCost') {
                propertyData.editableData.improvements = {
                  ...propertyData.editableData.improvements,
                  hasManualCost: !value,
                };
              } else {
                propertyData.editableData.improvements = {
                  ...propertyData.editableData.improvements,
                  [feature]: {
                    ...propertyData.editableData.improvements?.[feature],
                    hasFeature: !value,
                  },
                };
              }

              handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
            }
          }}
        />
      </Flex>
    );
  }

  return null;
};

export const TitleRow = (props: TitleRowProps) => {
  const { fieldTypes, title, isRemovable } = props;

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="surface4"
      height="32px"
      padding="8px"
      gridColumn="1 / -1"
      width="100%"
    >
      {fieldTypes.includes('label') ? <Typography variant="h3">{title}</Typography> : <Flex />}
      {isRemovable && <RemoveFeatureSectionComponent {...props} />}
    </Flex>
  );
};
